/* eslint-disable @typescript-eslint/no-explicit-any */
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import { withDatasourceCheck, Text, Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { BodyCopy } from 'src/helpers/BodyCopy';
import Headline from 'src/helpers/Headline/Headline';
import classNames from 'classnames';
import { useTheme } from 'lib/context/ThemeContext';
import { StarRating } from 'src/helpers/StarRating';
import { ProductMediaCardTheme } from './ProductMediaCard.theme';
import Card from 'src/helpers/Card/Card';
import { getHeadingLevel } from 'lib/utils/getHeadingLevel';
import Button from 'src/helpers/Button/Button';
import ImageWrapper from 'src/helpers/Media/ImageWrapper';
import { parseFilterRuleDataForAggregates, useReviewSummary } from 'lib/reviews/get-reviews-v2';
import { useAffiliate } from 'lib/context/AffiliateContext';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getEnum } from 'lib/utils/get-enum';
import SvgIcon from 'src/helpers/SvgIcon/SvgIcon';
import { getScaledThumbnailFieldFromPhoto } from 'src/lib/utils/photo-item-utils';
import RichTextWrapper from 'src/helpers/RichTextWrapper/RichTextWrapper';
import ProductMediaModal from 'src/helpers/Modals/ProductMediaModal';
import { getSourceNameFromVideoItem, playStopVideo } from 'src/helpers/Media/VideoUtils';
import Head from 'next/head';
import { normalizeSitecoreDateStringFormattedWithTime } from 'lib/utils/string-utils';
import ReactDOM from 'react-dom';
import ModalWrapper from 'src/helpers/ModalWrapper/ModalWrapper';
import VideoWrapper from 'src/helpers/Media/VideoWrapper';
import { getBreakpoint, useCurrentScreenType } from 'lib/utils/get-screen-type';
import { useModalIdContext } from 'lib/context/GenericModalIDContext';
import { useRouter } from 'next/router';
import { scrollVisualizerCardIntoView } from 'lib/utils/scroll-visualizer-card-utils';
import { Spinner } from 'src/helpers/Spinner';
import ModalPortal from 'src/helpers/Modals/ModalPortal';

export type ProductMediaCardProps = Feature.EnterpriseWeb.RenewalByAndersen.Cards.ProductMediaCard;

const ProductMediaCard = (props: ProductMediaCardProps) => {
  const { fields } = props;
  const productId = fields?.productItem?.displayName.replaceAll(' ', '');
  const { themeData, themeName } = useTheme(ProductMediaCardTheme);
  const { userAffiliate } = useAffiliate();
  const productFields: any = useMemo(() => {
    if (fields?.data) {
      const mappedFields = fields.data.item.fields.map((field: any) => {
        return { [field.name]: field.jsonValue };
      });
      const result = Object.assign({}, ...mappedFields);
      const mappedChildrenFields = fields.data.item.children?.results?.map((field: any) => {
        return {
          fields: Object.assign(
            {},
            ...field?.fields?.map((innerField: any) => {
              return { [innerField.name]: innerField.jsonValue };
            })
          ),
        };
      });

      result.children = mappedChildrenFields;

      return result;
    }

    return fields;
  }, [fields?.data]);
  const reviewModCategory = productFields?.productItem?.fields?.reviewModCategory?.map(
    (review: any) => {
      return review?.fields?.Value.value;
    }
  );
  const affiliateId =
    userAffiliate?.affiliateId && userAffiliate.affiliateId !== '0'
      ? [userAffiliate.affiliateId]
      : undefined;
  const filters = parseFilterRuleDataForAggregates(reviewModCategory, affiliateId);
  const productReview = useReviewSummary(filters);
  const averageRating = productReview?.averageRating || '';
  const totalProjectCount = productReview?.totalProjects || '';
  const mediaArray = [fields?.gif, fields?.productImage];
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(
    mediaArray.findIndex((x) => x?.fields?.thumbnailImage)
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { currentScreenWidth } = useCurrentScreenType();
  const isTablet =
    currentScreenWidth >= getBreakpoint('md') && currentScreenWidth < getBreakpoint('mml');
  const isDesktop = currentScreenWidth >= getBreakpoint('mml');
  const isCard = props.fields?.productModal?.templateName?.includes('Card') ? true : false;
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const modalId = isCard ? 'arProductModal_' + productId : 'arProductVis';
  const {
    selectedModalId,
    setSelectedModalId,
    prevFocusedElementRef,
    setIsGenericModalOpen,
    isGenericModalOpen,
  } = useModalIdContext();
  const [isGenericModalReset, setIsGenericModalReset] = useState(false);
  const swapProductID = getEnum<string>(props.fields?.productModal?.fields?.productId);
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  const openModalAndPlayVideo = () => {
    const productVideo = props.fields?.productVideo;
    setIsGenericModalOpen(true);
    setIsModalOpen(true);
    playStopVideo(
      (productVideo?.fields?.videoId as Field<string>)?.value || '',
      'playVideo',
      productVideo && getSourceNameFromVideoItem(productVideo)
    );
  };

  const formattedDate =
    (props.fields?.productVideo?.fields?.lastUpdated as Field<string>)?.value &&
    (props.fields?.productVideo?.fields?.lastUpdated as Field<string>)?.value !=
      '0001-01-01T00:00:00Z' &&
    normalizeSitecoreDateStringFormattedWithTime(
      (props.fields?.productVideo?.fields?.lastUpdated as Field<string>)?.value
    );

  const ldJsonScriptYouTube = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    contentURL: props.fields?.productVideo?.fields?.videoId
      ? `https://www.youtube.com/watch?v=${
          (props.fields?.productVideo?.fields?.videoId as Field<string>)?.value
        }`
      : '',
    description:
      (props.fields?.productVideo?.fields?.videoDescription as Field<string>)?.value || '',
    embedUrl: props.fields?.productVideo?.fields?.videoId
      ? `https://www.youtube.com/embed/${
          (props.fields?.productVideo?.fields?.videoId as Field<string>)?.value
        }`
      : '',
    name: (props.fields?.productVideo?.fields?.videoName as Field<string>)?.value || '',
    thumbnailUrl:
      props.fields?.productVideoThumbnail?.value?.src ||
      `https://i.ytimg.com/vi/${
        (props.fields?.productVideo?.fields?.videoId as Field<string>)?.value
      }/sddefault.jpg` ||
      '',
    uploadDate: formattedDate || '',
  };

  type ProductMediaCardThumbnailProps = {
    index: number;
    media: Feature.EnterpriseWeb.RenewalByAndersen.Data.Photos.Photo;
  };

  const RenderThumbnailElement = ({ index, media }: ProductMediaCardThumbnailProps) => {
    if (!media?.fields?.fullImage?.value?.src) {
      return <></>;
    }

    const scaledThumbnail = getScaledThumbnailFieldFromPhoto(media);

    return (
      <ImageWrapper
        key={index}
        image={{
          value: {
            src: scaledThumbnail?.value?.src,
            width: 52,
            height: 52,
            alt: media?.fields?.fullImage?.value?.alt,
          },
        }}
        imageLayout={'responsive'}
      />
    );
  };

  const loadingModal = () => {
    return (
      <ModalPortal>
        <ModalWrapper
          isModalOpen={isLoading}
          size={'fluid'}
          handleClose={() => setIsLoading(false)}
          modalLabel={modalId + '_loading'}
          customOverlayclass="flex !z-[1000]"
        >
          <div className="loader flex min-h-[100vh] w-[100vw] items-center justify-center lg:min-h-[45vh] lg:w-[63vw]">
            <Spinner size={48} />
          </div>
        </ModalWrapper>
      </ModalPortal>
    );
  };
  const setARShareUrl = (arProductID?: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('modalId', modalId + '_' + arProductID);
    url.searchParams.set('ar', 'true');

    router.push(url.toString(), undefined, { shallow: true });
  };

  const handleCTAOpen = (arProductID?: string) => {
    if (!isCard) {
      const currentProdId = arProductID ? arProductID : swapProductID;
      const h2 = document.querySelector('#visHeadline > div > h2');

      if (h2) {
        h2.textContent = props.fields?.headlineText?.value;
      }
      setARShareUrl(currentProdId);
      if ((window as any).tkSwapProduct) {
        (window as any).tkSwapProduct(currentProdId);
      }
    }
    setSelectedModalId(modalId);
  };

  const handleOpenProductModal = () => {
    setIsProductModalOpen(true);
  };

  const handleCloseProductModal = () => {
    if ((isGenericModalReset && modalId) || selectedModalId === modalId) {
      prevFocusedElementRef?.current?.focus();
      setIsProductModalOpen(false);
      setIsGenericModalOpen(false);
      setSelectedModalId('');
      setIsGenericModalReset(false);
      const cleanUrl = new URL(window.location.href);
      cleanUrl.search = '';
      router.push(cleanUrl.toString(), undefined, { shallow: true });
    }
  };

  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    //Account for extra modal opened on top
    if (!isGenericModalOpen && isProductModalOpen) {
      setIsGenericModalReset(true);
    } else {
      setIsGenericModalReset(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenericModalOpen]);

  useEffect(() => {
    if (isGenericModalReset) {
      setSelectedModalId(modalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGenericModalReset]);

  useEffect(() => {
    if (selectedModalId) {
      selectedModalId === modalId && handleOpenProductModal();
    }
    if (selectedModalId === '') {
      handleCloseProductModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModalId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const tkConfigId = params.get('tkConfigId');
    let modalFromUrl = params.get('modalId');
    const arEnabled = params.get('ar') === 'true';

    let timer: ReturnType<typeof setTimeout> | undefined;
    let index = 0;
    let modalProductId = '';
    if (!isCard) {
      index = modalFromUrl?.indexOf('_') || 0;
      modalProductId = modalFromUrl?.slice(index + 1) || '';
      modalFromUrl = modalFromUrl?.slice(0, index) || '';
    }

    if (tkConfigId && modalId === 'arProductVis' && !isCard && swapProductID === modalProductId) {
      setIsLoading(true);
      setSelectedModalId(modalId + '_loading');
      timer = setTimeout(() => {
        scrollVisualizerCardIntoView(cardRef.current);
        handleCTAOpen(modalProductId);
        setIsLoading(false);
      }, 4000);
    }

    if (!tkConfigId && arEnabled && modalFromUrl === modalId && isCard) {
      timer = setTimeout(() => {
        scrollVisualizerCardIntoView(cardRef.current);
        setSelectedModalId(modalId);
      }, 0);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const thumbsQty = isTablet
    ? 3
    : mediaArray.filter(Boolean).length + (props.fields?.productVideoThumbnail?.value?.src ? 1 : 0);

  const ctaMobileStyles: Record<number, string> = {
    3: 'w-full justify-start',
    2: 'flex-1 justify-end ml-4',
    1: 'flex-1 justify-end ml-4',
  };

  const viewInARBtn = () => {
    return (
      <>
        <div
          className={classNames(
            themeData.classes.buttonContainer,
            isDesktop ? 'ml-auto items-end justify-end ' : ctaMobileStyles[thumbsQty] || 'w-full'
          )}
        >
          <div
            className={classNames(
              'items-center justify-center',
              'flex items-start md:items-center'
            )}
          >
            {fields?.ctaDetailsText && (
              <div
                className={classNames(
                  'items-center justify-center',
                  'flex items-start md:items-center'
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCTAOpen(undefined);
                }}
              >
                <Button
                  field={{
                    value: {
                      text: fields?.ctaDetailsText?.value,
                    },
                  }}
                  variant={fields?.ctaDetailsStyle}
                  icon={fields?.ctaDetailsIcon}
                  classes={classNames(
                    fields?.ctaDetailsStyle,
                    !isDesktop && thumbsQty === 3 ? 'mt-[15px]' : ''
                  )}
                />
              </div>
            )}
          </div>
          {loadingModal()}
          <ProductMediaModal
            modalId={modalId}
            headlineText={props.fields?.headlineText?.value}
            isModalOpen={isProductModalOpen}
            setIsModalOpen={handleCloseProductModal}
            {...props}
          />
        </div>
      </>
    );
  };

  return (
    <Card dataComponent="card/productmediacard" {...props}>
      <div id={modalId} ref={cardRef} className={themeData.classes.cardWrapper}>
        <div className={themeData.classes.headlineWrapper}>
          <Headline
            useTag={getHeadingLevel('h5', props.fields?.headlineLevel)}
            classes={themeData.classes.headline}
            {...props}
          />
        </div>
        {averageRating && totalProjectCount && (
          <>
            <div className={themeData.classes.ratingsWrapper}>
              <div className={themeData.classes.ratingsIcons}>
                <StarRating reviewStars={averageRating} />
              </div>
              <div className={themeData.classes.ratingsText}>
                <Text
                  field={{
                    editable: averageRating.toFixed(1),
                    value: averageRating.toFixed(1),
                  }}
                />
              </div>
            </div>
            <div className={themeData.classes.totalProjectWrapper}>
              <div className={themeData.classes.totalProjectCount}>
                <Text field={{ value: totalProjectCount.toLocaleString() }} />
              </div>
              <div className={themeData.classes.totalProjectText}>
                <Text field={{ value: 'Projects completed in your area' }} />
              </div>
            </div>
          </>
        )}

        <div className={themeData.classes.mediaWrapper}>
          <ImageWrapper
            image={mediaArray[selectedMediaIndex]?.fields?.fullImage}
            imageLayout={'intrinsic'}
          />
        </div>
        <div className={themeData.classes.thumbnailButtonWrapper}>
          <div className={themeData.classes.thumbnailRowWrapper}>
            {mediaArray.map(
              (media, index) =>
                media?.fields?.thumbnailImage?.value?.src && (
                  <div
                    key={index}
                    className={classNames('mr-1 h-[3.4375rem] w-[3.4375rem] cursor-pointer')}
                  >
                    <div
                      className={classNames(
                        index === selectedMediaIndex &&
                          'relative before:absolute before:top-0 before:left-0 before:z-10 before:h-full before:w-full before:border-[0.25rem] before:border-primary'
                      )}
                      onClick={() => setSelectedMediaIndex(index)}
                    >
                      <RenderThumbnailElement index={index} media={media}></RenderThumbnailElement>
                    </div>
                  </div>
                )
            )}
            {props.fields?.productVideoThumbnail?.value?.src && (
              <>
                <Head>
                  {ldJsonScriptYouTube && (
                    <script
                      type="application/ld+json"
                      dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJsonScriptYouTube) }}
                    />
                  )}
                </Head>
                <div
                  key={'3'}
                  onKeyDown={(e) => {
                    if (e.code === 'Enter' || e.code === 'Space') {
                      openModalAndPlayVideo();
                    }
                  }}
                  onClick={() => openModalAndPlayVideo()}
                  className={classNames('h-[3.4375rem] w-[3.4375rem] cursor-pointer')}
                >
                  <div className="relative">
                    <span className="absolute top-1/2 left-1/2 z-[10] -translate-x-1/2 -translate-y-1/2">
                      <SvgIcon icon="play" size={26} />
                    </span>
                    <ImageWrapper
                      image={{
                        value: {
                          src: ldJsonScriptYouTube.thumbnailUrl,
                          width: 52,
                          height: 52,
                        },
                      }}
                      ratio={'square'}
                      imageLayout={'fill'}
                      additionalMobileClasses="h-[55px]"
                    />
                  </div>
                </div>
                {isModalOpen &&
                  ReactDOM.createPortal(
                    <ModalWrapper
                      size="w-full md:w-[65vw] md:max-w-[1199px] h-auto max-h-[540px] md:max-h-[753px]"
                      isModalOpen={isModalOpen}
                      handleClose={() => {
                        setIsModalOpen(false);
                        setIsGenericModalOpen(false);
                      }}
                    >
                      <div className="p-m">
                        <VideoWrapper
                          videoItem={props.fields?.productVideo}
                          videoThumbnailImage={props?.productVideoThumbnail}
                        />
                      </div>
                    </ModalWrapper>,
                    document.querySelector(`.${themeName}`) || document.body
                  )}
              </>
            )}
          </div>
          {props.fields?.productModal && viewInARBtn()}
        </div>
        <div className="flex grow flex-col">
          <div className={themeData.classes.copyWrapper}>
            {props.fields?.body && <BodyCopy classes={themeData.classes.body} {...props} />}
            {props.fields?.subheadline && (
              <RichTextWrapper
                field={{ value: fields.subheadline?.value }}
                classes={themeData.classes.subheadline}
              />
            )}
            {props.fields?.subheadline2 && (
              <RichTextWrapper
                field={{ value: fields.subheadline2?.value }}
                classes={themeData.classes.subheadline}
              />
            )}
          </div>
          <Button
            field={props.fields?.cta1Link}
            variant={props.fields?.cta1Style}
            icon={props.fields?.cta1Icon}
            modalId={
              (
                props.fields
                  ?.cta1Modal as unknown as Feature.EnterpriseWeb.Enterprise.Components.Modal.GenericModal.GenericModal
              )?.fields?.modalId?.value
            }
            modalLinkText={props.fields?.cta1ModalLinkText}
            classes={classNames(props.fields?.cta1Style, themeData.classes.cta1)}
          />
        </div>
      </div>
    </Card>
  );
};

export default withDatasourceCheck()<ProductMediaCardProps>(ProductMediaCard);
